import { loadDotenv } from "@x12i/env";
import { parsePreferOpenRouter } from "@x12i/ai-profiles";

/**
 * Env naming for vendor direct API keys: `{VENDOR}_API_KEY`
 * where VENDOR is the provider id in UPPER_SNAKE (hyphens → underscores).
 *
 * Examples: `OPENAI_API_KEY`, `ANTHROPIC_API_KEY`, `META_LLAMA_API_KEY`, `X_AI_API_KEY`
 */
export function providerIdToEnvKeyPrefix(providerId: string): string {
  return providerId
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
    .toUpperCase();
}

export function vendorApiKeyEnvName(providerId: string): string {
  return `${providerIdToEnvKeyPrefix(providerId)}_API_KEY`;
}

export type OpenRouterRoutingConfig = {
  /** OPENROUTER_API_KEY is set and non-empty */
  hasOpenRouterKey: boolean;
  /** From PREFER_OPENROUTER env; defaults to true when unset */
  preferOpenRouter: boolean;
  /** Read `{PROVIDER}_API_KEY` for a catalog provider id */
  getVendorApiKey(providerId: string): string | undefined;
};

/**
 * Load routing hints from process env and optional `.env` (via @x12/env).
 */
export function loadOpenRouterRoutingEnv(
  env: Record<string, string | undefined> = process.env,
): OpenRouterRoutingConfig {
  const dotenv = loadDotenv();
  const merged: Record<string, string> = {};
  for (const [k, v] of Object.entries(dotenv)) {
    if (v !== undefined) merged[k] = v;
  }
  for (const [k, v] of Object.entries(env)) {
    if (v !== undefined) merged[k] = v;
  }

  const openRouterKey = merged.OPENROUTER_API_KEY?.trim();
  const preferOpenRouter = parsePreferOpenRouter(merged.PREFER_OPENROUTER) ?? true;

  return {
    hasOpenRouterKey: Boolean(openRouterKey),
    preferOpenRouter,
    getVendorApiKey(providerId: string) {
      const name = vendorApiKeyEnvName(providerId);
      const val = merged[name]?.trim();
      return val || undefined;
    },
  };
}

/**
 * Default route via OpenRouter when:
 * 1. OPENROUTER_API_KEY is set AND PREFER_OPENROUTER is true (default), or
 * 2. OPENROUTER_API_KEY is set AND the vendor's `{VENDOR}_API_KEY` is missing.
 */
export function shouldDefaultRouteViaOpenRouter(
  providerId: string | undefined,
  config: OpenRouterRoutingConfig,
): boolean {
  if (!config.hasOpenRouterKey) return false;
  if (providerId && providerId !== "openrouter") {
    const vendorKey = config.getVendorApiKey(providerId);
    if (!vendorKey) return true;
    return config.preferOpenRouter;
  }
  return config.preferOpenRouter;
}

export type EffectiveOpenRouterTransportInput = {
  provider?: string;
  modelId?: string;
  routeViaOpenRouter?: boolean;
};

/**
 * Whether the effective runtime transport is OpenRouter.
 * Honors explicit gateway override, then env defaults from {@link loadOpenRouterRoutingEnv}.
 */
export function isEffectiveOpenRouterTransport(
  config: OpenRouterRoutingConfig,
  input: EffectiveOpenRouterTransportInput = {},
): boolean {
  if (input.routeViaOpenRouter === true) return true;
  if (input.routeViaOpenRouter === false) return false;

  const provider = input.provider?.trim().toLowerCase();
  if (provider === "openrouter") return true;

  const vendor =
    input.modelId?.includes("/") === true
      ? input.modelId.split("/")[0]!.trim().toLowerCase()
      : provider;

  if (vendor && vendor !== "openrouter") {
    return shouldDefaultRouteViaOpenRouter(vendor, config);
  }

  if (config.hasOpenRouterKey && config.preferOpenRouter) return true;
  return false;
}
